import React from "react";
import "./HeroEvents.css";

const pad = (n) => String(n).padStart(2, "0");
// Local image used if an event's banner art fails to load (so the hero is
// never blank — the /esmaaash_web/... paths only resolve on the live site).
const FALLBACK_IMG = "/dummy/dummy-event-1600x900.png";

// Split a future gap into days/hrs/min/sec. `over` => the event has started.
function breakdown(targetMs, nowMs) {
  const total = targetMs - nowMs;
  if (total <= 0) return { over: true, d: "00", h: "00", m: "00", s: "00" };
  return {
    over: false,
    d: pad(Math.floor(total / 86400000)),
    h: pad(Math.floor(total / 3600000) % 24),
    m: pad(Math.floor(total / 60000) % 60),
    s: pad(Math.floor(total / 1000) % 60),
  };
}

const isExternal = (href = "") => /^https?:/.test(href);

const onImgError = (e) => {
  if (!e.currentTarget.src.endsWith(FALLBACK_IMG)) e.currentTarget.src = FALLBACK_IMG;
};

/**
 * Single event hero: the event banner IMAGE shown in full (like before, never
 * blank thanks to a local fallback), with the "perfect" upcoming-style event
 * card — date, name, venue and a boxed days:hrs:min:sec countdown — below it.
 */
export default function HeroEvents({
  slides = [],
  active = 0,
  onSelect = () => {},
  now = Date.now(),
  viewerLabel = "",
  streamLive = false,
}) {
  const N = slides.length;
  if (!N) return null;

  const ev = slides[active % N];
  const targetMs = ev.date ? new Date(ev.date).getTime() : NaN;
  const c = Number.isNaN(targetMs) ? null : breakdown(targetMs, now);
  const live = ev.status === "live" || (c && c.over);
  const dateLabel = ev.date
    ? new Date(ev.date)
        .toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
        .toUpperCase()
    : "";

  return (
    <div className="hero-row">
      <div className="hero hero--events">
        <a
          className="hero-slide"
          href={ev.link}
          target={isExternal(ev.link) ? "_blank" : undefined}
          rel="noopener noreferrer"
        >
          {/* blurred fill behind the banner so the taller box has no empty
              bands while the FULL banner stays uncropped on top */}
          <img
            className="hero-events__bg"
            src={ev.image || FALLBACK_IMG}
            alt=""
            aria-hidden="true"
            onError={onImgError}
          />
          {/* Responsive banner: the compact phone image is used on small
              screens, the wide image on larger ones — swaps automatically. */}
          <picture>
            {ev.mobileImage && (
              <source media="(max-width: 768px)" srcSet={ev.mobileImage} />
            )}
            <img
              className="main-banner"
              src={ev.image || FALLBACK_IMG}
              alt={ev.title}
              loading="eager"
              decoding="async"
              onError={onImgError}
            />
          </picture>
        </a>

        {/* status badge — top-left */}
        <div className="hero-event-badges" aria-hidden="true">
          {live ? (
            <span className="hero-event-badge is-live">
              <span className="hero-event-dot" /> LIVE NOW
            </span>
          ) : (
            <span className="hero-event-badge">UPCOMING</span>
          )}
          {viewerLabel && (
            <span className="hero-event-badge hero-event-badge--views">
              {streamLive ? "🔴 " : ""}
              {viewerLabel}
            </span>
          )}
        </div>

        {/* small Register CTA on the banner */}
        <a
          className="hero-cta hero-cta--onbanner"
          href={ev.link}
          target={isExternal(ev.link) ? "_blank" : undefined}
          rel="noopener noreferrer"
        >
          <span className="hero-cta__icon">▶</span> REGISTER NOW
        </a>

        {/* dots — one per banner; click to jump */}
        {N > 1 && (
          <div className="hero-dots">
            {slides.map((s, idx) => (
              <button
                key={s.id ?? idx}
                type="button"
                className={`hero-dot${idx === active % N ? " is-active" : ""}`}
                aria-label={`Show ${s.title}`}
                onClick={() => onSelect(idx)}
              />
            ))}
          </div>
        )}
      </div>

      {/* "Perfect" upcoming-style event card — below the banner */}
      <div className={`hero-eventcard${live ? " is-live" : ""}`}>
        <span className="hero-eventcard__date">
          {c && c.over ? "Live now" : dateLabel || "Upcoming"}
        </span>
        <h3 className="hero-eventcard__name">{ev.title}</h3>
        {ev.venue && <span className="hero-eventcard__venue">{ev.venue}</span>}

        {live ? (
          <b className="hero-eventcard__live">🔴 HAPPENING NOW</b>
        ) : c ? (
          <div
            className="hero-cd"
            aria-label={`Starts in ${c.d} days ${c.h} hours ${c.m} minutes ${c.s} seconds`}
          >
            <span className="hero-cd__seg"><b>{c.d}</b><i>Days</i></span>
            <span className="hero-cd__sep">:</span>
            <span className="hero-cd__seg"><b>{c.h}</b><i>Hrs</i></span>
            <span className="hero-cd__sep">:</span>
            <span className="hero-cd__seg"><b>{c.m}</b><i>Min</i></span>
            <span className="hero-cd__sep">:</span>
            <span className="hero-cd__seg"><b>{c.s}</b><i>Sec</i></span>
          </div>
        ) : null}
      </div>
    </div>
  );
}
